// Riel de modulos (D26): la columna angosta de la izquierda con los modulos (Inicio, Bandeja, Clientes, Ventas,
// Reportes; Administracion abajo) y su numero de pendientes, y al final el menu de la persona (estado, perfil,
// telefono, salir). Las secciones de cada modulo viven en el panel de al lado (PanelModulo).
// Celular (D100): barra abajo con los cuatro primeros modulos y «Más», que sube una hoja con los demas modulos y el
// menu de la persona.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../auth/session';
import { useMovil, usePhone } from '../hooks';
import { empresaDeWorkspace, empresaDelHost, urlConfiguracionAvanzada } from '../lib/direcciones';
import { Icon } from '../lib/icons';
import { phone, PRESENCIAS, type Presencia } from '../phone/store';
import { Avatar } from '../ui/bits';
import { toast } from '../ui/toasts';
import type { Modulo } from './menu';
import { cuentaDeModulo, type Cuenta, destinoDeModulo, useContadores, useNavegacion } from './navegacion';

// La configuracion avanzada (panel tecnico del motor) es solo para el equipo Xmartek.
const EMPRESA_XMARTEK = import.meta.env.VITE_EMPRESA_XMARTEK || 'xmartek';
const esXmartek = (url?: string) => !!url && empresaDeWorkspace(url) === EMPRESA_XMARTEK;
// Modulos que caben en la barra del celular antes de «Más».
const EN_BARRA = 4;

const insignia = (c: Cuenta) => (c.n ? <span className={`b ${c.tono ?? ''}`}>{c.n > 99 ? '99+' : c.n}</span> : null);

export function Riel() {
  const { session, yo, salir, esAdmin } = useSession();
  const s = usePhone();
  const nav = useNavigate();
  const movil = useMovil();
  const { modulos, actual } = useNavegacion();
  const contadores = useContadores();
  const [menu, setMenu] = useState<DOMRect | null>(null);
  const [mas, setMas] = useState(false);

  const salirDe = (que: string) => {
    setMenu(null);
    setMas(false);
    if (s.call) return toast(`Termina la llamada antes de ${que}.`, 'info');
    phone.disconnect();
    salir();
  };
  const ir = (r: string) => (setMenu(null), setMas(false), nav(r));

  // Al pasar a escritorio (girar una tableta) la hoja de «Más» no tiene sentido.
  useEffect(() => {
    if (!movil) setMas(false);
  }, [movil]);

  const boton = (m: Modulo) => {
    const c = cuentaDeModulo(m, contadores);
    const activo = actual?.modulo.id === m.id;
    return (
      <Link
        key={m.id}
        to={destinoDeModulo(m)}
        className="mod"
        aria-current={activo ? 'page' : undefined}
        aria-label={c.n ? `${m.nombre}, ${c.n} pendientes` : m.nombre}
        data-testid={`mod-${m.id}`}
        // Novedad (Balizas): fuera de Reportes, la baliza de "Llamadas 3CX" va en el boton del modulo.
        data-baliza={!activo && m.secciones.some((s) => s.ruta === '/reportes/3cx') ? 'reportes-3cx' : undefined}
      >
        <Icon name={m.icono} />
        <span className="t">{m.id === 'admin' ? 'Admin' : m.nombre}</span>
        {insignia(c)}
      </Link>
    );
  };

  // Lo mismo en el menu del avatar (escritorio) y en la hoja de «Más» (celular).
  const opcionesPersona = (
    <>
      <p className="lbl" style={{ padding: '4px 10px 0' }}>
        Tu estado
      </p>
      <div className="menu-estados" role="group" aria-label="Tu estado">
        {(Object.keys(PRESENCIAS) as Presencia[]).map((p) => (
          <button key={p} role="menuitemradio" aria-checked={s.presencia === p} onClick={() => (phone.setPresencia(p), setMenu(null))}>
            <span className={`idot ${p}`} /> {PRESENCIAS[p].nombre}
          </button>
        ))}
      </div>
      <hr />
      <button role="menuitem" onClick={() => ir('/perfil')}>
        <Icon name="user" /> Mi perfil
      </button>
      <button role="menuitem" onClick={() => (setMenu(null), setMas(false), phone.expand('ajustes'))}>
        <Icon name="phone" /> Configurar teléfono
      </button>
      {esAdmin && esXmartek(session.workspace.url) ? (
        <a role="menuitem" href={urlConfiguracionAvanzada(session.workspace.url!)} target="_blank" rel="noreferrer" onClick={() => (setMenu(null), setMas(false))}>
          <Icon name="external" /> Configuración avanzada
        </a>
      ) : null}
      <hr />
      {!empresaDelHost() ? (
        <button role="menuitem" onClick={() => salirDe('cambiar de empresa')}>
          <Icon name="swap" /> Cambiar de empresa
        </button>
      ) : null}
      <button role="menuitem" onClick={() => salirDe('salir')}>
        <Icon name="logout" /> Salir
      </button>
    </>
  );

  if (movil) {
    // Si caben todos (cinco o menos) no hace falta «Más».
    const todos = [...modulos.filter((m) => !m.abajo), ...modulos.filter((m) => m.abajo)];
    const enBarra = todos.length <= EN_BARRA + 1 ? todos : todos.slice(0, EN_BARRA);
    const resto = todos.slice(enBarra.length);
    const cuentaResto = resto.reduce<Cuenta>(
      (a, m) => {
        const c = cuentaDeModulo(m, contadores);
        return { n: a.n + c.n, tono: a.tono ?? c.tono };
      },
      { n: 0 },
    );
    const enResto = !!actual && resto.some((m) => m.id === actual.modulo.id);
    return (
      <nav className="riel" aria-label="Módulos">
        {enBarra.map(boton)}
        {resto.length ? (
          <button className="mod" aria-current={enResto ? 'page' : undefined} aria-expanded={mas} aria-haspopup="dialog" onClick={() => setMas(true)} data-testid="mod-mas">
            <Icon name="dots" />
            <span className="t">Más</span>
            {insignia(cuentaResto)}
          </button>
        ) : null}
        {mas
          ? createPortal(
              <>
                <div className="hoja-scrim" onClick={() => setMas(false)} />
                <div className="hoja-movil hoja-mas" role="dialog" aria-label="Más">
                  <div className="hoja-agarre" aria-hidden>
                    <span />
                  </div>
                  <div className="mas-yo">
                    <Avatar nombre={yo.nombre} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{yo.nombre}</div>
                      <div className="note">{session.workspace.nombre}</div>
                    </div>
                    <span className={`idot ${s.presencia}`} style={{ marginLeft: 'auto' }} />
                  </div>
                  {resto.map((m) => (
                    <section key={m.id} className="mas-mod">
                      <p className="lbl">{m.nombre}</p>
                      <div className="mas-secciones">
                        {m.secciones.map((sec) => {
                          const c = sec.contador ? contadores[sec.contador] : null;
                          return (
                            <Link key={sec.ruta} to={sec.ruta} onClick={() => setMas(false)} aria-current={actual?.seccion.ruta === sec.ruta ? 'page' : undefined}>
                              <Icon name={sec.icono} />
                              <span className="t">{sec.nombre}</span>
                              {c?.n ? <span className={`cnt ${c.tono ?? ''}`}>{c.n}</span> : null}
                            </Link>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                  <div className="menu mas-persona">{opcionesPersona}</div>
                </div>
              </>,
              document.body,
            )
          : null}
      </nav>
    );
  }

  return (
    <nav className="riel" aria-label="Módulos">
      <Link to="/hoy" className="riel-marca" aria-label={`CRM XmarteK · ${session.workspace.nombre}`} title={session.workspace.nombre}>
        <Icon name="wave" />
      </Link>
      <div className="riel-mods">{modulos.filter((m) => !m.abajo).map(boton)}</div>
      <div className="riel-abajo">
        {modulos.filter((m) => m.abajo).map(boton)}
        <button className="riel-yo" onClick={(e) => setMenu(e.currentTarget.getBoundingClientRect())} aria-haspopup="menu" aria-label={`${yo.nombre} · ${PRESENCIAS[s.presencia].nombre}. Tu perfil y opciones`}>
          <Avatar nombre={yo.nombre} size="sm" />
          <span className={`idot ${s.presencia}`} />
        </button>
      </div>

      {menu
        ? createPortal(
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 69 }} onClick={() => setMenu(null)} />
              <div className="menu" role="menu" style={{ bottom: Math.max(8, window.innerHeight - menu.bottom), left: menu.right + 8 }}>
                <div className="mh">
                  <div style={{ fontWeight: 600 }}>{yo.nombre}</div>
                  <div className="note">{session.modo === 'usuario' ? yo.email : 'Conectado con API key'}</div>
                  <div className="note">{session.workspace.nombre}</div>
                </div>
                <hr />
                {opcionesPersona}
              </div>
            </>,
            document.body,
          )
        : null}
    </nav>
  );
}
